import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getInspectionList,
  getInspectionGeneratedReports,
  previewInspectionDocument,
  downloadInspectionDocument,
} from "../../helpers/backend_helper";
import { journeyPath } from "../../components/InspectionJourney/InspectionJourneyNav";

export default function EngagementReporting({ cycleId, engagementId }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const response = await getInspectionList();
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to load engagement reviews."
          );
        const reviews = (response.data || []).filter(
          (review) => Number(review.engagementId) === Number(engagementId)
        );
        const results = await Promise.all(
          reviews.map(async (review) => {
            try {
              const reports = await getInspectionGeneratedReports(review.id);
              if (!reports?.status)
                throw new Error(
                  reports?.message || "Unable to load reports for this review."
                );
              return { review, reports: reports.data || [] };
            } catch (reason) {
              return { review, reports: [], error: reason.message };
            }
          })
        );
        if (current) setGroups(results);
      } catch (reason) {
        if (current) setError(reason.message);
      } finally {
        if (current) setLoading(false);
      }
    })();
    return () => {
      current = false;
    };
  }, [engagementId, revision]);

  const openReport = async (review, report, download) => {
    setBusy(`${review.id}-${report.id}`);
    setError("");
    try {
      if (download)
        await downloadInspectionDocument(
          review.id,
          report.id,
          report.fileName || report.name || "report.pdf"
        );
      else await previewInspectionDocument(review.id, report.id);
    } catch (reason) {
      setError(reason.message || "Unable to open the report.");
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="inspection-journey__panel">
      <header>
        <h2>Reporting</h2>
        <button
          className="inspection-journey__button piers-refresh-button"
          onClick={() => setRevision((value) => value + 1)}
          disabled={loading}
        >
          Refresh
        </button>
      </header>
      <p>
        Reports appear here when generated by review submission and sign-off.
      </p>
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">Loading reports…</p>
      ) : groups.length ? (
        groups.map(({ review, reports, error: reportError }) => (
          <section className="inspection-journey__panel" key={review.id}>
            <header>
              <div>
                <h2>{review.inspectionNo}</h2>
                <span>
                  {review.inspectionStatus?.name || "Status unavailable"}
                </span>
              </div>
              <Link
                to={`${journeyPath(
                  cycleId,
                  engagementId,
                  "reviews"
                )}?inspectionId=${review.id}&workspace=1`}
              >
                Open Review &amp; Sign-offs →
              </Link>
            </header>
            {reportError ? (
              <p role="alert">{reportError}</p>
            ) : reports.length ? (
              reports.map((report) => (
                <article className="inspection-journey__report" key={report.id}>
                  <div>
                    <strong>
                      {report.fileName || report.name || "Generated report"}
                    </strong>
                    <small>
                      {report.createdOn
                        ? new Date(report.createdOn).toLocaleString()
                        : ""}
                    </small>
                  </div>
                  <button
                    className="inspection-journey__button"
                    disabled={busy === `${review.id}-${report.id}`}
                    onClick={() => openReport(review, report, false)}
                  >
                    Preview
                  </button>
                  <button
                    className="inspection-journey__button"
                    disabled={busy === `${review.id}-${report.id}`}
                    onClick={() => openReport(review, report, true)}
                  >
                    Download
                  </button>
                </article>
              ))
            ) : (
              <p>
                No report has been generated for this review yet. Open the
                review to see the remaining work and sign-offs.
              </p>
            )}
          </section>
        ))
      ) : (
        !error && (
          <p>
            No reviews are available for this engagement yet. Complete
            preparation and submit it for review to continue.
          </p>
        )
      )}
      <Link to={journeyPath(cycleId, engagementId, "reviews")}>
        ← Back to Reviews
      </Link>
    </section>
  );
}
